import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import dayjs, { Dayjs } from 'dayjs';
import { PrimaryButton, SecondaryButton } from '../../../components/atoms/Button';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  GetTechnicianAttendanceCalendar,
  IAttendanceCalendarDay,
  IAttendanceCalendarSummary,
} from '../../../services/technician';
import { DetailedAttendanceCalendar } from './DetailedAttendanceCalendar';
import { EditAttendanceDialog } from './EditAttendanceDialog';
import {
  ATTENDANCE_TYPE_COLORS as TYPE_COLORS,
  getComputedStatusColor,
  resolveDayDisplayStatus,
  resolveWorkingStatus,
} from '../../../utils/attendanceDisplay';
import { AttendanceTimeBlock, WorkingStatusChip } from './AttendanceTimeBlock';
import { AttendanceStatusChip } from './AttendanceStatusChip';

const calendarSx = {
  '& .MuiCalendarPicker-root': {
    width: '100%',
    maxWidth: 360,
    margin: '0 auto',
    backgroundColor: 'transparent',
  },
  '& .MuiPickersCalendarHeader-root': {
    px: 1,
    mt: 0,
    mb: 1.5,
  },
  '& .MuiPickersCalendarHeader-label': {
    fontWeight: 600,
    fontSize: '1.05rem',
    color: '#f5f5f5',
    letterSpacing: '0.01em',
  },
  '& .MuiPickersCalendarHeader-switchViewButton': {
    display: 'none',
  },
  '& .MuiPickersArrowSwitcher-root button': {
    color: '#9ca3af',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
    },
  },
  '& .MuiDayPicker-header': {
    justifyContent: 'space-around',
    mb: 0.5,
  },
  '& .MuiDayPicker-weekDayLabel': {
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    width: 40,
    height: 28,
  },
  '& .MuiDayPicker-monthContainer': {
    overflow: 'visible',
  },
  '& .MuiDayPicker-slideTransition': {
    minHeight: 252,
  },
};

interface AttendanceCalendarProps {
  technicianId: string;
}

const SummaryRow = ({
  label,
  value,
  color,
  circular = false,
}: {
  label: string;
  value: string;
  color: string;
  circular?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography variant="body1" color="#9ca3af" sx={{ fontSize: '1rem' }}>
        {label}
      </Typography>
    </Box>
    {circular ? (
      <Box
        sx={{
          minWidth: 32,
          height: 32,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          border: `2px solid ${color}`,
          backgroundColor: `${color}1A`,
          color,
          fontSize: '0.875rem',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {value}
      </Box>
    ) : (
      <Typography variant="body2" fontWeight={600} color="#f3f4f6">
        {value}
      </Typography>
    )}
  </Box>
);

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '3px',
        backgroundColor: `${color}33`,
        border: `1.5px solid ${color}`,
      }}
    />
    <Typography variant="caption" color="#9ca3af" sx={{ fontSize: '0.7rem' }}>
      {label}
    </Typography>
  </Box>
);

export const AttendanceCalendar = ({ technicianId }: AttendanceCalendarProps) => {
  const today = dayjs();
  const [days, setDays] = useState<IAttendanceCalendarDay[]>([]);
  const [summary, setSummary] = useState<IAttendanceCalendarSummary | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(today);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [openModal, setOpenModal] = useState(false);
  const [modalDate, setModalDate] = useState<Dayjs | null>(null);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendRefreshKey, setExtendRefreshKey] = useState(0);

  const [visibleMonth, setVisibleMonth] = useState<Dayjs>(
    today.startOf('month'),
  );

  const visibleMonthKey = visibleMonth.format('YYYY-MM');

  useEffect(() => {
    fetchAttendance(visibleMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when month/technician changes
  }, [technicianId, visibleMonthKey]);

  const fetchAttendance = async (monthStart: Dayjs) => {
    try {
      // Same engine-backed endpoint as the detailed calendar so the compact
      // card can never disagree with it.
      const response = await GetTechnicianAttendanceCalendar({
        technicianId,
        month: monthStart.month() + 1,
        year: monthStart.year(),
      });
      setDays(response.data);
      setSummary(response.summary);
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
    }
  };

  const attendanceMap = new Map(days.map((day) => [day.date, day]));

  const selectedDateStr = selectedDate?.format('YYYY-MM-DD');
  const selectedRecord = selectedDateStr
    ? attendanceMap.get(selectedDateStr)
    : undefined;

  const openEditDialog = (day: Dayjs) => {
    setModalDate(day);
    setSelectedDate(day);
    setOpenModal(true);
  };

  const modalDay = modalDate
    ? attendanceMap.get(modalDate.format('YYYY-MM-DD'))
    : undefined;
  // FUTURE days carry no editable record yet — let the dialog use its defaults.
  const modalRecord =
    modalDay && modalDay.status !== 'FUTURE' ? modalDay : undefined;

  return (
    <Card
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: '#1C1C1C',
        border: '1px solid #2a2a2a',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        gap: 3,
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
      }}
    >
      {SnackbarComponent}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600} color="#f5f5f5">
            Attendance & Leaves
          </Typography>
          <Tooltip title="Full attendance view">
            <IconButton
              size="small"
              onClick={() => {
                setExtendRefreshKey(Date.now());
                setExtendOpen(true);
              }}
              aria-label="Full attendance view"
              sx={{
                color: '#E31E25',
                border: '1px solid rgba(227, 30, 37, 0.45)',
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(227, 30, 37, 0.12)',
                  borderColor: '#E31E25',
                },
              }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={calendarSx}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CalendarPicker
              date={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              onMonthChange={(newMonth) => setVisibleMonth(dayjs(newMonth))}
              renderDay={(day) => {
                const dateStr = day.format('YYYY-MM-DD');
                const record = attendanceMap.get(dateStr);
                const displayStatus =
                  record && record.status !== 'FUTURE'
                    ? resolveDayDisplayStatus(
                        record.computedStatus ?? record.status,
                        record.events,
                      )
                    : null;
                const isSelected = selectedDate?.isSame(day, 'day');
                const isToday = day.isSame(today, 'day');
                const isOutsideMonth = !day.isSame(visibleMonth, 'month');
                const statusColor = displayStatus
                  ? getComputedStatusColor(displayStatus)
                  : undefined;

                return (
                  <Box
                    key={dateStr}
                    sx={{
                      position: 'relative',
                      width: 44,
                      height: 44,
                      m: '2px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      backgroundColor: statusColor
                        ? `${statusColor}1A`
                        : isSelected
                          ? 'rgba(227, 30, 37, 0.15)'
                          : isToday
                            ? 'rgba(255,255,255,0.05)'
                            : 'transparent',
                      border: isSelected
                        ? '2px solid #E31E25'
                        : isToday
                          ? '1px solid #404040'
                          : '1px solid transparent',
                      color: isOutsideMonth ? '#4b5563' : '#e5e7eb',
                      transition: 'background-color 0.15s ease, border-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: statusColor
                          ? `${statusColor}28`
                          : isSelected
                            ? 'rgba(227, 30, 37, 0.22)'
                            : 'rgba(255,255,255,0.08)',
                      },
                      ...(statusColor && {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 5,
                          width: 14,
                          height: 3,
                          borderRadius: 2,
                          backgroundColor: statusColor,
                        },
                      }),
                    }}
                    onClick={() => {
                      setSelectedDate(day);
                      openEditDialog(day);
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.9375rem',
                        fontWeight: isSelected || isToday ? 700 : 500,
                        lineHeight: 1,
                      }}
                    >
                      {day.date()}
                    </Typography>
                  </Box>
                );
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: '1px solid #2a2a2a',
          }}
        >
          <LegendItem color={TYPE_COLORS.PRESENT} label="Present" />
          <LegendItem color={TYPE_COLORS.LATE_CHECK_IN} label="Late Check-In" />
          <LegendItem color={TYPE_COLORS.HALF_DAY} label="Half Day" />
          <LegendItem color={TYPE_COLORS.ABSENT} label="Absent" />
          <LegendItem color={TYPE_COLORS.LEAVE} label="Leave" />
          <LegendItem color={TYPE_COLORS.HOLIDAY} label="Holiday" />
          <LegendItem color={TYPE_COLORS.WEEKLY_OFF} label="Weekly Off" />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          backgroundColor: '#212121',
          borderRadius: 2,
          border: '1px solid #2a2a2a',
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          color: '#e0e0e0',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            mb: 2,
            color: '#f5f5f5',
            borderBottom: '1px solid #333',
            pb: 1.5,
          }}
        >
          {visibleMonth.format('MMMM YYYY')} Summary
        </Typography>

        <SummaryRow
          label="Present Days"
          value={String(summary?.presentDays ?? 0)}
          color={TYPE_COLORS.PRESENT}
          circular
        />
        <SummaryRow
          label="Late Check-In"
          value={String(summary?.lateComing ?? 0)}
          color={TYPE_COLORS.LATE_CHECK_IN}
          circular
        />
        <SummaryRow
          label="Half Days"
          value={String(summary?.halfDayWork ?? 0)}
          color={TYPE_COLORS.HALF_DAY}
          circular
        />
        <SummaryRow
          label="Absent Days"
          value={String(summary?.absentDays ?? 0)}
          color={TYPE_COLORS.ABSENT}
          circular
        />
        <SummaryRow
          label="Leaves Taken"
          value={String(summary?.leavesTaken ?? 0)}
          color={TYPE_COLORS.LEAVE}
          circular
        />
        <SummaryRow
          label="Holidays"
          value={String(summary?.holiday ?? 0)}
          color={TYPE_COLORS.HOLIDAY}
          circular
        />
        <SummaryRow
          label="Weekly Off"
          value={String(summary?.weeklyOff ?? summary?.sunday ?? 0)}
          color={TYPE_COLORS.WEEKLY_OFF}
          circular
        />

        {selectedDate && (
          <>
            <Divider sx={{ my: 2, borderColor: '#333' }} />

            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: '#f5f5f5' }}
            >
              {selectedDate.format('DD MMM YYYY')}
            </Typography>

            {selectedRecord && selectedRecord.status !== 'FUTURE' ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.75,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="#9ca3af"
                    sx={{ fontSize: '1rem' }}
                  >
                    Status
                  </Typography>
                  <AttendanceStatusChip
                    status={resolveDayDisplayStatus(
                      selectedRecord.computedStatus ?? selectedRecord.status,
                      selectedRecord.events,
                    )}
                    withHint
                  />
                </Box>
                {selectedRecord.checkInTime && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <WorkingStatusChip
                      status={resolveWorkingStatus({
                        type: 'PRESENT',
                        checkInTime: selectedRecord.checkInTime,
                        checkOutTime: selectedRecord.checkOutTime,
                        attendanceDate: selectedRecord.date,
                      })}
                    />
                  </Box>
                )}

                {(selectedRecord.checkInTime || selectedRecord.checkOutTime) && (
                  <AttendanceTimeBlock
                    attendanceDate={selectedRecord.date}
                    checkInTime={selectedRecord.checkInTime}
                    checkOutTime={selectedRecord.checkOutTime}
                    totalMinutes={selectedRecord.workedMinutes}
                  />
                )}

                {selectedRecord.description?.trim() && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="#6b7280">
                      Note
                    </Typography>
                    <Typography variant="body2" color="#d1d5db" sx={{ mt: 0.25 }}>
                      {selectedRecord.description}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <PrimaryButton
                    title="Edit Attendance"
                    onClick={() => selectedDate && openEditDialog(selectedDate)}
                  />
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="#6b7280">
                  No attendance marked for this day.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <PrimaryButton
                    title="Mark Attendance"
                    onClick={() => selectedDate && openEditDialog(selectedDate)}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <EditAttendanceDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        technicianId={technicianId}
        date={modalDate}
        record={modalRecord}
        onSaved={() => {
          fetchAttendance(visibleMonth);
          setExtendRefreshKey(Date.now());
        }}
        onSuccess={(message) => showSnackbar(message, 'success')}
        onError={(message) => showSnackbar(message, 'error')}
      />

      <Dialog
        open={extendOpen}
        onClose={() => setExtendOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: '#111',
            color: '#fff',
            width: '96vw',
            maxWidth: 1400,
            maxHeight: '92vh',
            borderRadius: 2,
            border: '1px solid #333',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: '1px solid #2a2a2a',
            backgroundColor: '#111',
          }}
        >
          Technician Detailed Attendance
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#111', p: 2 }}>
          {extendOpen && (
            <DetailedAttendanceCalendar
              key={extendRefreshKey}
              technicianId={technicianId}
              month={visibleMonth}
              onMonthChange={setVisibleMonth}
              onAttendanceSaved={() => fetchAttendance(visibleMonth)}
              onSuccess={(message) => showSnackbar(message, 'success')}
              onError={(message) => showSnackbar(message, 'error')}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#111', borderTop: '1px solid #2a2a2a' }}>
          <SecondaryButton title="Close" onClick={() => setExtendOpen(false)} />
        </DialogActions>
      </Dialog>
    </Card>
  );
};
